import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Zap, Trophy, TrendingUp, Calendar, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getSportCategories, getSportEmoji, getSportCategory, isFixtureLocked, getCountdown } from "@/lib/fixtures";
import { getPoints, getUser, apiGetPredictions, apiGetCrossUniLeaderboard, apiGetResults, apiGetFixtures } from "@/lib/storage";
import { UCL_LOGO, KCL_LOGO, useSlip } from "@/components/Layout";

export default function Dashboard() {
  const navigate = useNavigate();
  const { addToSlip, isItemInSlip } = useSlip();
  const [points, setPoints] = useState(getPoints());
  const [predictions, setPredictions] = useState([]);
  const [crossBoard, setCrossBoard] = useState([]);
  const [results, setResults] = useState([]);
  const [fixtures, setFixtures] = useState([]);

  useEffect(() => {
    const handler = () => setPoints(getPoints());
    window.addEventListener("predictions-updated", handler);
    apiGetPredictions().then(setPredictions);
    apiGetCrossUniLeaderboard().then(d => setCrossBoard(d.standings || []));
    apiGetResults().then(setResults);
    apiGetFixtures().then(setFixtures);
    return () => window.removeEventListener("predictions-updated", handler);
  }, []);

  const predMap = {};
  predictions.forEach(p => { predMap[p.fixture_id] = p; });
  const resultMap = {};
  results.forEach(r => { resultMap[r.fixture_id] = r; });

  const wonCount = predictions.filter(p => p.status === "won").length;
  const lostCount = predictions.filter(p => p.status === "lost").length;
  const scoredCount = wonCount + lostCount;
  const accuracy = scoredCount > 0 ? Math.round((wonCount / scoredCount) * 100) : 0;
  const categories = getSportCategories(fixtures);
  const openFixtures = fixtures.filter(f => !isFixtureLocked(f) && !resultMap[f.id]);
  const user = getUser();

  return (
    <div data-testid="dashboard-page">
      {/* Hero */}
      <div className="hero-gradient p-8 lg:p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl">
          <p className="text-[10px] text-red-500 font-bold uppercase tracking-[0.3em] mb-3">BUCS Fantasy Predictions</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {user?.username ? `Welcome, ${user.username}` : "Welcome to Hunch"}
          </h1>
          <p className="text-sm text-slate-400 mb-6 max-w-lg">
            Make your predictions for <span className="text-red-400 font-semibold">{fixtures.length} fixture{fixtures.length !== 1 ? "s" : ""}</span>.
            Earn <span className="text-green-400 font-semibold">fantasy points</span> for every correct pick.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <button data-testid="hero-view-fixtures" onClick={() => navigate("/fixtures")}
              className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors">
              View Fixtures <ArrowRight size={14} />
            </button>
            <div className="flex items-center gap-3">
              <img src={UCL_LOGO} alt="UCL" className="h-8 object-contain" />
              <span className="text-xs text-slate-600 font-bold">VS</span>
              <img src={KCL_LOGO} alt="KCL" className="h-8 object-contain" />
            </div>
            {user?.university && (
              <Badge className="text-[10px] bg-white/5 text-slate-400 border-white/10">
                Representing {user.university}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Points", value: points, icon: Zap, color: "text-red-400" },
            { label: "Predictions", value: predictions.length, icon: Trophy, color: "text-blue-400" },
            { label: "Accuracy", value: `${accuracy}%`, icon: TrendingUp, color: "text-amber-400" },
            { label: "Wins", value: wonCount, icon: CheckCircle, color: "text-green-400" },
          ].map(stat => (
            <div key={stat.label} className="bg-[#111827] border border-white/5 rounded-sm p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-2 mb-1">
                <stat.icon size={14} className={stat.color} />
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">{stat.label}</span>
              </div>
              <p className={`text-2xl font-bold font-mono ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Cross-Uni Standings Quick View */}
        {crossBoard.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>UNIVERSITY STANDINGS</h2>
              <button data-testid="view-leaderboard" onClick={() => navigate("/leaderboard")}
                className="text-[10px] text-red-500 hover:text-red-400 uppercase tracking-widest font-semibold flex items-center gap-1">
                Full Leaderboard <ArrowRight size={10} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {crossBoard.slice(0, 4).map((uni, i) => (
                <div key={uni.university} className={`bg-[#111827] border border-white/5 rounded-sm p-4 ${uni.university === user?.university ? "border-red-500/30" : ""}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-bold font-mono text-slate-500">#{i + 1}</span>
                    <span className="text-sm font-bold text-white">{uni.university}</span>
                  </div>
                  <p className="text-xl font-bold font-mono text-red-400">{uni.total_points}</p>
                  <p className="text-[10px] text-slate-600">{uni.member_count} members</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sport Categories */}
        <div>
          <p className="text-[10px] text-red-500 font-bold uppercase tracking-[0.2em] mb-3">Browse by Sport</p>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
            {categories.map(cat => (
              <div key={cat} className="cat-icon-btn border border-white/5 flex-shrink-0" onClick={() => navigate(`/sport/${encodeURIComponent(cat)}`)} data-testid={`cat-btn-${cat}`}>
                <span className="text-2xl">{getSportEmoji(cat)}</span>
                <span className="text-[10px] text-slate-400 capitalize whitespace-nowrap">{cat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming (only open, unresulted fixtures) */}
        {openFixtures.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>UPCOMING FIXTURES</h2>
              <button data-testid="view-all-upcoming" onClick={() => navigate("/fixtures")}
                className="text-[10px] text-red-500 hover:text-red-400 uppercase tracking-widest font-semibold flex items-center gap-1">
                View All <ArrowRight size={10} />
              </button>
            </div>
            <div className="bg-[#111827] border border-white/5 rounded-sm overflow-hidden">
              {openFixtures.slice(0, 6).map(fixture => {
                const pred = predMap[fixture.id];
                const slipItem = isItemInSlip(fixture.id);
                return (
                  <div key={fixture.id} className="match-row" data-testid={`upcoming-${fixture.id}`}>
                    <span className="text-base mr-3">{getSportEmoji(fixture.sport)}</span>
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-xs font-semibold text-white truncate">{fixture.sport}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{fixture.date} | {fixture.time}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1 font-mono mr-4">
                      <Clock size={8} /> {getCountdown(fixture)}
                    </Badge>
                    {pred ? (
                      <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30">
                        {pred.team === "A" ? "UCL" : "KCL"} picked
                      </Badge>
                    ) : (
                      <div className="flex gap-1.5">
                        <button data-testid={`home-${fixture.id}-ucl`} onClick={() => addToSlip(fixture.id, "A", fixture)}
                          className={`pick-btn text-xs rounded-sm ${slipItem?.team === "A" ? "selected" : ""}`}>
                          <img src={UCL_LOGO} alt="" className="w-4 h-4 object-contain" /> UCL
                        </button>
                        <button data-testid={`home-${fixture.id}-kcl`} onClick={() => addToSlip(fixture.id, "B", fixture)}
                          className={`pick-btn text-xs rounded-sm ${slipItem?.team === "B" ? "selected" : ""}`}>
                          <img src={KCL_LOGO} alt="" className="w-4 h-4 object-contain" /> KCL
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
